import { IdentityEditableEmail } from '../../../../components/editable-email/IdentityEditableEmail';
import { Identity } from '@startuptools/business-ui/identity/Identity';
import { EmailStatus } from '../../../../components/email-status/EmailStatus';
import { Button } from '@startuptools/ui/Button';
import { Trans } from '@lingui/react';
import { type GqlFileQuery, useSendMailMeetingSummonsToOneMutation } from '../../../../graphql/react-operations';
import { GqlEmailSimplifiedState } from '../../../../graphql/base-types.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import {
  type GqlParticipantsSummonsQuery,
  ParticipantsSummonsDocument,
  type useDocGenerateMeetingSummonsDirectMutation,
} from './ParticipantsSummons.graphql';
import { type ArrayElement } from '@startuptools/common/common';
import { isNil } from 'lodash-es';
import { type Dispatch, type SetStateAction } from 'react';
import { GqlDocGenState } from '../../../../graphql/base-types.graphql';
import { ShareholdersMeetingDocument } from '../../../shareholders-meetings/ShareholdersMeeting.graphql';
import { BoardMeetingDocument } from '../../../board-meetings/BoardMeeting.graphql';

export const ParticipantSummonsRow = ({
  participant,
  summonsDoc,
  meetingId,
  loading,
  doGenerate,
  setShowSnackbar,
}: {
  participant: ArrayElement<GqlParticipantsSummonsQuery['meetingParticipants']['models']>;
  summonsDoc: GqlFileQuery['file'] | null | undefined;
  meetingId: string;
  loading: boolean;
  doGenerate: ReturnType<typeof useDocGenerateMeetingSummonsDirectMutation>[0];
  setShowSnackbar: Dispatch<SetStateAction<boolean>>;
}) => {
  const companyId = useCompanyId();
  const [doSend, { loading: doSendLoading }] = useSendMailMeetingSummonsToOneMutation({
    refetchQueries: [ShareholdersMeetingDocument, BoardMeetingDocument, ParticipantsSummonsDocument],
  });

  const handleSend = async () => {
    let documentId = summonsDoc?.id;
    let genState = summonsDoc?.genState;
    if (!summonsDoc || summonsDoc.dirty || summonsDoc.genState === GqlDocGenState.Failed) {
      const { data } = await doGenerate();
      if (!data) {
        return;
      }
      documentId = data.docGenerateMeetingSummonsDirect.id;
      genState = data.docGenerateMeetingSummonsDirect.genState;
    }
    if (!isNil(genState) && genState !== GqlDocGenState.Completed) {
      // Toast to have them wait for generation
      setShowSnackbar(true);
      return;
    }
    if (!documentId) {
      throw new Error('documentId undefined when it should not be');
    }
    await doSend({
      variables: { companyId, input: { meetingId, fileId: documentId, meetingParticipantId: participant.id } },
    });
  };

  const identity = participant.proxy ?? participant.identity;

  return (
    <tr>
      <td>
        <Identity identity={participant.identity} editable={false} />
      </td>
      <td>{participant.proxy && <Identity identity={participant.proxy} editable={false} />}</td>
      <td>
        <IdentityEditableEmail id={identity.id} refetchQueries={[ParticipantsSummonsDocument]}>
          {identity.email}
        </IdentityEditableEmail>
      </td>
      <td>
        <EmailStatus
          emailState={participant.latestMeetingParticipantEmail?.email.emailStatus}
          failReason={participant.latestMeetingParticipantEmail?.email.emailFailReason}
          currentEmail={identity.email}
          summonsEmail={participant.latestMeetingParticipantEmail?.email.toEmail}
          summonsDocId={summonsDoc?.id}
          emailSummonsId={participant.latestMeetingParticipantEmail?.fileId}
          jobState={participant.latestMeetingParticipantEmail?.email.jobState}
        />
      </td>
      <td align="right">
        <Button
          variant="outlined"
          size="sm"
          disabled={
            !identity.email ||
            loading ||
            participant.latestMeetingParticipantEmail?.email.emailSimplifiedState === GqlEmailSimplifiedState.Pending
          }
          loading={doSendLoading}
          onClick={handleSend}
        >
          <Trans id="Send" />
        </Button>
      </td>
    </tr>
  );
};
